import {
    addCalendarDays,
    calendarDayEndInZone,
    calendarDayStartInZone,
    zonedDayKey,
    zonedInstant,
    type DayKey
} from '@cineamo/ui/src/helpers/cinema-time-zone';

/**
 * Which day a showing belongs to on the cinema website — the **calendar day it starts on, read on
 * the cinema's clock**.
 *
 * This used to be the Cinema Day (04:00 to 03:59 the next morning, CONTEXT.md), which files a 01:00
 * screening under the previous evening. That is not how a guest reads a date filter. Someone
 * picking "Saturday" and finding a Friday-night screening in the list — or picking Friday at 01:00
 * and being shown a program that has already finished — reads it as a bug, which is what it was
 * reported as (CIN-15254). The boundary is now plain midnight.
 *
 * The zone is always the cinema's, never the browser's: these sites serve German, Austrian and Swiss
 * cinemas and a guest can read them from anywhere. `Date#setHours` would read the *viewer's* zone,
 * so a guest in New York would get day buttons labeled one day early and a "today" filter running
 * 00:00 to 23:59 New York — a window that drops that evening's screenings and picks up the previous
 * night's. A day is therefore carried as a **day key** (`YYYY-MM-DD`), which names the same day in
 * every zone and is also what `selectedDate` already is in the URL.
 */

/**
 * A value that names a calendar day rather than an instant: a bare `YYYY-MM-DD`, or the same day
 * serialized as UTC midnight (how the API ships a movie's release date).
 */
const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?(Z|\+00:00))?$/;

/**
 * The day key `value` names: a calendar day (see `CALENDAR_DAY_PATTERN`) is taken verbatim, anything
 * else is an instant resolved on the cinema's clock.
 *
 * The verbatim branch matters for the values that *name a calendar day* — a `selectedDate` from the
 * URL, a movie's release date. `new Date('2026-10-01')` is UTC midnight, which is still Sept 30 for
 * every zone west of Greenwich and lands one day early there. It must not catch a showing's
 * `startDatetime`, though: that is UTC too, and a 00:30 Berlin screening on Oct 17 starts at
 * `2026-10-16T22:30:00Z`, whose leading ten characters name the wrong day.
 */
export function programDayKey(value: string | Date, timeZone: string): DayKey {
    if (typeof value === 'string' && CALENDAR_DAY_PATTERN.test(value)) {
        return value.slice(0, 10);
    }
    return zonedDayKey(new Date(value), timeZone);
}

/** The day key of the calendar day running right now at the cinema. */
export function todayProgramDayKey(timeZone: string): DayKey {
    return zonedDayKey(new Date(), timeZone);
}

/** The day key `days` calendar days after today at the cinema (negative to step back). */
export function programDayKeyFromToday(days: number, timeZone: string): DayKey {
    return addCalendarDays(todayProgramDayKey(timeZone), days);
}

/** Midnight at the cinema on the day `value` names — the first instant of that day. */
export function programDayStart(value: string | Date, timeZone: string): Date {
    return calendarDayStartInZone(programDayKey(value, timeZone), timeZone);
}

/** 23:59:59.999 at the cinema on the day `value` names — its last instant. */
export function programDayEnd(value: string | Date, timeZone: string): Date {
    return calendarDayEndInZone(programDayKey(value, timeZone), timeZone);
}

/** Midnight at the cinema today — the earliest instant any "from now on" listing reaches back to. */
export function startOfProgramToday(timeZone: string): Date {
    return calendarDayStartInZone(todayProgramDayKey(timeZone), timeZone);
}

/**
 * The instant the cinema's clock reads `hour`:00 on the day `value` names — the time-of-day
 * filter's bounds. Resolved through the zone rather than by adding hours to midnight, so the
 * hour the filter names is the hour on the wall even on a DST day.
 */
export function programDayAtHour(value: string | Date, hour: number, timeZone: string): Date {
    return zonedInstant(programDayKey(value, timeZone), hour, timeZone);
}

/**
 * The `visibleDays` day keys the calendar bar renders, starting today at the cinema.
 *
 * Keys rather than `Date`s: the bar's labels, the day counts and the query window all have to agree
 * on which day is which, and only a key says that unambiguously from another zone.
 */
export function programDayKeys(timeZone: string, visibleDays = 14): DayKey[] {
    const today = todayProgramDayKey(timeZone);
    return Array.from({ length: visibleDays }, (_, index) => addCalendarDays(today, index));
}
